import { flatten } from 'lodash-es';
import { LAYOUT, ASSET_TYPES_WITH_MAX_1_RENDERER_IN_HORIZONTAL_LAYOUT } from '../constants/Constants';
import { OpenInOfficeOnlineHelper } from '../helpers/OpenInOfficeOnlineHelper';
import { AssetViewer } from './AssetViewer';
import { PdfViewerPresentationRenderer } from './renderers/PdfViewerPresentationRenderer';
import { UrlIframePresentationRenderer } from './renderers/UrlIframePresentationRenderer';
import { UrlPresentationRenderer } from './renderers/UrlPresentationRenderer';
import { RenderState } from '../types/RenderState.type';
export class RenderingQueue {
    constructor(config) {
        this.config = config;
        this.thumbnailViewerActive = true;
        this.onIdle = null;
    }
    setViewer(viewer) {
        this.viewer = viewer;
    }
    setThumbnailViewer(thumbnailViewer) {
        this.thumbnailViewer = thumbnailViewer;
    }
    renderHighestPriority() {
        // reset idleTimeout
        if (this.idleTimeout) {
            clearTimeout(this.idleTimeout);
            this.idleTimeout = null;
        }
        // Pages have a higher priority than thumbnails, so check them first.
        if (this.viewer.forceRendering()) {
            return;
        }
        // No pages needed rendering, so check thumbnails.
        if (this.thumbnailViewer && this.thumbnailViewerActive) {
            if (this.thumbnailViewer.forceRendering()) {
                return;
            }
        }
        if (this.viewer.currentView &&
            this.viewer.currentView.presentationRenderer &&
            this.viewer.currentView.presentationRenderer.assetViewer &&
            this.viewer.currentView.presentationRenderer.assetViewer.thumbnailViewer &&
            this.viewer.currentView.presentationRenderer.assetViewer.thumbnailViewer.active &&
            this.thumbnailViewerActive) {
            if (this.viewer.currentView.presentationRenderer.assetViewer.thumbnailViewer.forceRendering()) {
                return;
            }
        }
        this.checkIdle();
    }
    checkIdle() {
        if (this.onIdle) {
            // TODO take thumbnailViewer into account?
            // TODO take nested viewers into account?
            const currentlyRendering = this.viewer.views.some(view => view.renderState === RenderState.Running);
            if (!currentlyRendering) {
                this.idleTimeout = setTimeout(this.onIdle, this.config.IDLE_DEBOUNCE_TIMEOUT);
            }
        }
    }
    getHighestPriorityView(viewState) {
        const allVisibleViews = this.getAllVisibleViews(viewState);
        const focussedView = this.getFocussedView(viewState);
        const focussedViewIndex = allVisibleViews.findIndex(view => view.view === focussedView.view);
        // const firstPriorityView = focussedView.parentView ? focussedView.parentView : focussedView.view;
        // const firstPriorityViewIndex = allVisibleViews.indexOf(firstPriorityView);
        const sortedVisibleViews = [
            focussedView,
            ...allVisibleViews.slice(0, focussedViewIndex),
            ...allVisibleViews.slice(focussedViewIndex + 1, allVisibleViews.length)
        ];
        const numVisible = sortedVisibleViews.length;
        if (numVisible === 0) {
            return null;
        }
        for (let i = 0; i < numVisible; i++) {
            const view = sortedVisibleViews[i];
            if (view.parentView && this.shouldRenderView(view.parentView)) {
                return { view: view.parentView };
            }
            else if (this.shouldRenderView(view.view)) {
                return view;
            }
        }
        // const navigatingForward = viewState.scroll.down;
        //
        // // All the visible views have rendered, try to render next/previous pages.
        // if (navigatingForward) {
        //     const nextSlideIndex = viewState.last.id + 1;
        //     if (this.views[nextSlideIndex] && this.shouldRenderView(this.views[nextSlideIndex])) {
        //         return this.views[nextSlideIndex];
        //     }
        // } else {
        //     const previousSlideIndex = viewState.first.id - 1;
        //     if (this.views[previousSlideIndex] && this.shouldRenderView(this.views[previousSlideIndex])) {
        //         return this.views[previousSlideIndex];
        //     }
        // }
        return null;
    }
    getHighestPriorityViews(viewState) {
        const allVisibleViews = this.getAllVisibleViews(viewState);
        if (!(allVisibleViews && allVisibleViews.length > 0)) {
            return null;
        }
        const focussedView = this.getFocussedView(viewState);
        const focussedViewIndex = allVisibleViews.findIndex(view => view.view === focussedView.view);
        const sortedVisibleViews = [
            focussedView,
            ...allVisibleViews.slice(0, focussedViewIndex),
            ...allVisibleViews.slice(focussedViewIndex + 1, allVisibleViews.length)
        ];
        return sortedVisibleViews
            .map(view => {
            if (view.parentView && this.shouldRenderView(view.parentView)) {
                return { view: view.parentView };
            }
            else if (this.shouldRenderView(view.view)) {
                return view;
            }
            return null;
        })
            .filter(view => !!view);
    }
    renderView(view, renderPreview = false) {
        // TODO add check for 'renderPreview'?
        if (!this.shouldRenderView(view)) {
            return;
        }
        const renderFn = renderPreview ? view.renderPreview : view.render;
        renderFn().then(() => {
            if (view.presentationRenderer instanceof UrlPresentationRenderer ||
                view.presentationRenderer instanceof UrlIframePresentationRenderer ||
                OpenInOfficeOnlineHelper.shouldShowPresentInOfficeOnlineBar(view)) {
                // calculate height based on the DOM element after it has been added to the DOM
                view.viewerLayout.update();
            }
            this.renderHighestPriority();
        });
    }
    cancelNonVisibleViews(viewState, views) {
        const visibleAssetViewerViews = viewState.views.map(view => view.view);
        views.forEach(view => {
            if (view.renderState === RenderState.Running && visibleAssetViewerViews.indexOf(view) === -1) {
                view.cancel();
            }
        });
    }
    getAllVisibleViews(viewState) {
        return flatten(viewState.views
            .map(visibleView => visibleView.view)
            .map(assetViewerView => {
            if (assetViewerView.presentationRenderer && assetViewerView.presentationRenderer.assetViewer instanceof AssetViewer) {
                if (assetViewerView.presentationRenderer.assetViewer.viewer.viewerLayout.viewState &&
                    assetViewerView.presentationRenderer.assetViewer.viewer.viewerLayout.viewState.views.length) {
                    const visibleNestedViews = assetViewerView.presentationRenderer.assetViewer.viewer.viewerLayout.viewState.views.map(visibleView => visibleView.view);
                    // return [assetViewerView].concat(visibleNestedViews);
                    return visibleNestedViews.map(view => ({ view, parentView: assetViewerView }));
                }
                else {
                    return { view: assetViewerView };
                }
            }
            else {
                return { view: assetViewerView };
            }
        }));
    }
    getFocussedView(viewState) {
        var _a;
        const focussedView = viewState.focus.view;
        if (((_a = focussedView.presentationRenderer) === null || _a === void 0 ? void 0 : _a.assetViewer) instanceof AssetViewer &&
            focussedView.presentationRenderer.assetViewer.viewer.viewerLayout.viewState &&
            focussedView.presentationRenderer.assetViewer.viewer.viewerLayout.viewState.focus.view) {
            return {
                view: focussedView.presentationRenderer.assetViewer.viewer.viewerLayout.viewState.focus.view,
                parentView: focussedView
            };
        }
        else {
            return { view: focussedView };
        }
    }
    isViewFinished(view) {
        var _a;
        // if (this.config.DEBUG) {
        // TODO can we remove this content rendered check? This used to be and issue in IE or Edge
        const contentRendered = ((_a = view.presentationRenderer) === null || _a === void 0 ? void 0 : _a.assetViewer) || view.presentationRenderer instanceof PdfViewerPresentationRenderer
            ? true
            : view.contentElement.childElementCount > 0;
        if (this.config.DEBUG && !contentRendered && view.renderState === RenderState.Finished) {
            // TODO
            console.warn('investigate', view);
        }
        return view.renderState === RenderState.Finished && contentRendered;
        // }
        // return view.renderState === RenderState.Finished;
    }
    isViewRendering(view) {
        return view.renderState === RenderState.Running;
    }
    isViewLoading(view) {
        return view.isLoading;
    }
    isViewRenderingDisabled(view) {
        return (this.config.VIEWER_LAYOUT === LAYOUT.HORIZONTAL &&
            ASSET_TYPES_WITH_MAX_1_RENDERER_IN_HORIZONTAL_LAYOUT.includes(view.assetViewerItem.type) &&
            view !== this.viewer.currentView);
    }
    isViewCollapsed(view) {
        return view.parentView && !view.parentView.isExpanded;
    }
    shouldRenderView(view) {
        return (!this.isViewFinished(view) &&
            !this.isViewLoading(view) &&
            !this.isViewRendering(view) &&
            !this.isViewRenderingDisabled(view) &&
            !this.isViewCollapsed(view));
    }
}
//# sourceMappingURL=RenderingQueue.js.map